import { useNavigate } from "react-router-dom"
import { formatDate, formatOrdinal, PATH_INFO } from "../utils/helper"
import { useCallback, useEffect, useRef, useState } from "react"
import fileImg from '../../assets/images/icons/file-upload.svg'
import { Modal } from "../../components/Modal"
import errorImg from '../../assets/images/icons/errorOutline.svg'
import InfiniteScroll from "react-infinite-scroll-component"
import ReactLoading from "react-loading"

type SpecType = {
  created_at: string
  part_number: string
  sample_number: string
  kind: string
  repeat: number
  size: string
  color_no: string
  time: number
  id: string
}

type SpecMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
  nextPage: number | null
}

type SpecApiResponse = {
  data: SpecType[]
  meta: SpecMeta
}

const SPEC_PAGE_LIMIT = 50
const DEFAULT_SPEC_META: SpecMeta = {
  total: 0,
  page: 1,
  limit: SPEC_PAGE_LIMIT,
  hasMore: false,
  nextPage: null
}
const SPEC_TABLE_SCROLL_ID = "spec-import-table-scroll"

type ImportErrorDetail = {
  message: string
  rowNumber?: number
  value?: string[]
}

export function Specification() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [specList, setSpecList] = useState<SpecType[]>([])
  const specMetaRef = useRef<SpecMeta>(DEFAULT_SPEC_META)
  const [specMeta, setSpecMeta] = useState<SpecMeta>(DEFAULT_SPEC_META)
  const [isSpecLoading, setIsSpecLoading] = useState(false)
  const [isSpecLoaded, setIsSpecLoaded] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [time, setTime] = useState('')
  const [isModal, setIsModal] = useState(false)
  const [isErrorModal, setIsErrorModal] = useState(false)
  const [uploadError, setUploadError] = useState<{ row: number[], message: string }>()
  const [importErrorDetail, setImportErrorDetail] = useState<ImportErrorDetail | null>(null)
  const onCloseErrorModal = useCallback(() => {
    setIsErrorModal(false)
    setImportErrorDetail(null)
  }, [])

  useEffect(() => {
    specMetaRef.current = specMeta
  }, [specMeta])

  useEffect(() => {
    setFile(null)
    setTime('')
  }, [])

  const fetchSpecifications = useCallback(async (options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = specMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }
    try {
      setIsSpecLoading(true)
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(SPEC_PAGE_LIMIT)
      })
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec?${params.toString()}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      })
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
      }

      const data = await res.json() as SpecApiResponse
      const items: SpecType[] = Array.isArray(data?.data) ? data.data : []
      setSpecList(prev => {
        if (!shouldAppend) {
          return items
        }
        const map = new Map<string, SpecType>()
        prev.forEach(item => map.set(item.id, item))
        items.forEach((item: SpecType) => map.set(item.id, item))
        return Array.from(map.values())
      })
      setSpecMeta(data.meta ?? DEFAULT_SPEC_META)
    } catch (error) {
      console.log('ERROR FETCH')
    } finally {
      setIsSpecLoading(false)
      setIsSpecLoaded(true)
    }
  }, [navigate])

  const loadMoreSpecifications = useCallback(async () => {
    if (isSpecLoading || !specMetaRef.current.hasMore) {
      return
    }
    await fetchSpecifications({ append: true })
  }, [fetchSpecifications, isSpecLoading])

  useEffect(() => {
    fetchSpecifications()
  }, [fetchSpecifications])

  const resetFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setFile(null)
    setTime('')
  }, [])

  const handleFileUpload = useCallback(async (onDuplicateMode?: 'skip' | 'overwrite') => {
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('time', time)
    if (onDuplicateMode) {
      formData.append('onDuplicate', onDuplicateMode)
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec/import/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        },
        body: formData
      })

      if (res.status === 409) {
        const data = await res.json()
        setUploadError({
          row: data.duplicates.map((el: { row: number }) => el.row),
          message: data.message
        })
        setIsModal(true)
        return
      }

      if (!res.ok) {
        let errorBody: Partial<ImportErrorDetail> | null = null
        try {
          errorBody = await res.json()
        } catch (parseError) {
          errorBody = null
        }
        setImportErrorDetail({
          message: errorBody?.message || 'エラーが発生しました。',
          rowNumber: errorBody?.rowNumber,
          value: errorBody?.value
        })
        setIsErrorModal(true)
        return
      }

      await fetchSpecifications()
      resetFileInput()
    } catch (error) {
      console.error('Error uploading file')
      resetFileInput()
    }
  }, [file, time, fetchSpecifications, resetFileInput])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0]
      setFile(dropped)
    }
  }, [])

  return (
    <div className="relative bg-gray-100 rounded-[10px] pt-[22px] pb-[30px] px-[25px] mt-[25px] mx-[40px] min-w-[575px]">
      <div className='flex items-center w-full'>
        <div className='w-2 h-[35px] rounded-sm bg-[#3E3E3E] mr-[5px]' ></div>
        <span className='text-[18px]'>
          仕様書インポート
        </span>
      </div>
      <div className="flex justify-center flex-col-reverse pt-[25px] desktop-md:flex-row desktop-md:space-y-0">
        <Tabele
          specList={specList}
          hasMore={specMeta.hasMore}
          onLoadMore={loadMoreSpecifications}
          isLoaded={isSpecLoaded}
        />
        <div className="flex flex-col w-full justify-between mb-2.5 desktop-md:max-w-[1016px] desktop-md:pl-[20px] desktop-md:mb-0">
          <div
            className="flex items-center justify-center w-full h-[372px] bg-[#F9FAFF] rounded-[23px] desktop-md:h-[630px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="flex items-center justify-center w-[192px] h-[192px] bg-[#E1E2FF] rounded-[50%]">
              <img src={fileImg} alt="file" className="w-[115px]" />
            </div>
            <div className="relative flex flex-col items-center ml-[26px]">
              <span className="mb-[9px] text-[#545899] font-bold">仕様書をドロップ</span>
              <span className="mb-[12px] text-[#545899] font-bold">または</span>
              <div
                className='flex items-center w-[165px] h-[41px] border-[2px] border-[#545899] rounded-[8px] text-[#545899] font-bold bg-white'
              >
                <input
                  type="file"
                  id='file-input'
                  accept='.csv'
                  ref={fileInputRef}
                  className='sr-only'
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFile(e.target.files[0])
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                />
                <label htmlFor='file-input' className='flex items-center justify-center cursor-pointer w-full h-full'>
                  <span>
                    ファイルを選択
                  </span>
                </label>
              </div>
              <span className="absolute -bottom-[30px] flex max-w-[165px] min-w-0 items-center space-x-[5px] pt-[5px]">
                <p
                  className="flex-1 truncate"
                  title={file?.name || ''}
                  aria-label={file?.name || ''}
                >
                  {file?.name}
                </p>
                {file && <button onClick={() => setFile(null)}>✕</button>}
              </span>
            </div>
          </div>
          <div className="flex justify-center items-center w-full space-x-[14px] mt-[15px] desktop-md:mt-0">
            <button onClick={() => navigate(PATH_INFO.HOME.PATH)} className="bg-black w-[158px] h-[37px] rounded-[50px] tablet-lg:w-[200px] tablet-lg:h-[47px]">
              <span className="text-white text-sm tablet-lg:text-base">検寸画面に戻る</span>
            </button>
            <button
              className="w-[143px] h-[37px] rounded-[50px] bg-[#C5C7FF] tablet-lg:w-[200px] tablet-lg:h-[47px]"
              onClick={() => handleFileUpload()}
            >
              <span className="text-[#545899] font-bold text-sm tablet-lg:text-base">インポート</span>
            </button>
            <label className="space-x-[5px] h-max">
              <span className="text-[#545899] font-bold">サンプル状況 :</span>
              <select
                className="h-[30px] bg-transparent text-center pr-[5px] text-[#474980] border-0 border-b-2 border-[#545899] focus:outline-none focus:ring-0 focus:border-[#3a3c69] peer"
                value={time || ''}
                onChange={(e) => { setTime(e.target.value) }}
              >
                <option value={''} hidden>--</option>
                <option value={'1'}>1st</option>
                <option value={'2'}>2nd</option>
                <option value={'3'}>3rd</option>
                <option value={'4'}>4th</option>
                <option value={'5'}>5th</option>
              </select>
            </label>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModal}
        onClose={() => setIsModal(false)}
        className="min-w-[450px] max-w-[1000px] min-h-[250px] max-h-fit flex flex-col items-center justify-center p-[25px]"
      >
        <span className="text-[16px] text-center">
          <span>CSVの{uploadError?.row.map((el) => el + 1).join(', ')}行目と既存データが重複しています。</span>
          <p>上書きしますか？</p>
        </span>
        <div className="space-x-[25px] mt-[35px]">
          <button
            className="border p-2 rounded-[25px] w-[150px] border-[#666]"
            onClick={() => {
              handleFileUpload('skip')
              setIsModal(false)
            }}
          >
            <p className="text-[#666] font-bold">いいえ</p>
          </button>
          <button
            className="border p-2 rounded-[25px] w-[150px] bg-[#C5C7FF]"
            onClick={() => {
              handleFileUpload('overwrite')
              setIsModal(false)
            }}>
            <p className="text-[#545899] font-bold">はい</p>
          </button>
        </div>
      </Modal>
      <ErrorModal isOpen={isErrorModal} onClose={onCloseErrorModal} detail={importErrorDetail} />
    </div>
  )
}

function Tabele({ specList, hasMore, onLoadMore, isLoaded }: { specList: SpecType[], hasMore: boolean, onLoadMore: () => void, isLoaded: boolean }) {
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
  const [sortedSpecList, setSortedSpecList] = useState<SpecType[]>([])
  const endMessage = isLoaded && !hasMore
    ? <div className="py-3 text-center text-xs text-[#666666]">表示可能な履歴はすべて読み込み済みです。</div>
    : (
      <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
        <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
        <span>仕様書履歴を読み込み中です…</span>
      </div>
    )

  useEffect(() => {
    if (Array.isArray(specList)) {
      const sorted = specList.slice().sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
      })
      setSortedSpecList(sorted)
    }
  }, [specList, sortOrder])

  return (
    <div
      className="desktop-md:bg-white desktop-md:rounded-[20px] desktop-md:pt-5 desktop-md:pb-[30px] desktop-md:px-4 desktop-md:shadow-[0px_4px_4px_0px_#00000040]"
    >
      <div className='flex items-center justify-between w-full pb-[10px] pl-[9px] pr-[14px]'>
        <span className='text-[16px] opacity-0 desktop-md:opacity-100'>
          仕様書インポート履歴
        </span>
        <div className="flex space-x-[8px]">
          <label className="flex items-center">
            <input
              type="radio"
              name="sort"
              className="w-[26px] h-[26px] mr-[8px] accent-[#545899]"
              checked={sortOrder === 'asc'}
              value="asc"
              onChange={() => setSortOrder('asc')}
            />
            撮影日順(昇順)
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="sort"
              className="w-[26px] h-[26px] mr-[8px] accent-[#545899]"
              checked={sortOrder === 'desc'}
              value="desc"
              onChange={() => setSortOrder('desc')}
            />
            撮影日順(降順)
          </label>
        </div>
      </div>
      <div className="h-[380px] overflow-y-scroll border border-gray-400 bg-white desktop-md:w-[655px] desktop-md:h-[660px]" id={SPEC_TABLE_SCROLL_ID}>
        <InfiniteScroll
          dataLength={sortedSpecList.length}
          next={onLoadMore}
          hasMore={hasMore}
          loader={
            <div className="flex items-center justify-center py-3 text-center text-xs text-[#666666]">
              <ReactLoading type="spin" color="#666666" height={16} width={16} className="inline-block mr-2" />
              <span>仕様書履歴を読み込み中です…</span>
            </div>}
          endMessage={endMessage}
          scrollableTarget={SPEC_TABLE_SCROLL_ID}
          scrollThreshold="200px"
          style={{ overflow: "visible" }}
        >
          <table className="w-full">
            <thead className="bg-[#3E3E3E] h-[23px]">
              <tr className="sticky inset-0 bg-[#3E3E3E] text-[12px] text-white">
                <th className="sticky top-0 w-[165px] font-normal">検寸日時</th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  品番
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サンプルNo
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サンプル状況
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  リピート
                </th>
                <th className="sticky top-0 w-[80px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  サイズ
                </th>
                <th className="sticky top-0 w-[115px] font-normal before:content-[''] before:absolute before:inset-0 before:border-l before:border-white">
                  カラー
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedSpecList.map((el: SpecType, index: number) => (
                <tr key={index} className='text-[14px] h-[30px]'>
                  <td className="h-[30px] border border-l-0 border-t-0 border-gray-400 text-center text-[16px]">{formatDate(el.created_at)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.part_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.sample_number || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.kind === 'final' ? 'FINAL' : formatOrdinal(el.time)}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.repeat > 0 ? el.repeat : 0}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.size || ''}</td>
                  <td className="border border-t-0 border-gray-400 text-center">{el.color_no || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  )
}

function ErrorModal({ isOpen, onClose, detail }: { isOpen: boolean, onClose: () => void, detail: ImportErrorDetail | null }) {
  const hasValue = detail?.value?.length && detail.value[0] !== ''

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="min-w-[480px] max-w-fit max-h-[460px] flex flex-col items-center justify-center p-6"
    >
      <div className="w-full">
        <div className="flex items-center justify-center">
          <img src={errorImg} alt="error" className="w-6 mr-2" />
          <p className="text-center font-bold text-[16px]">
            {detail?.message || 'エラーが発生しました。'}
          </p>
        </div>
        {detail?.value && (
          <p className="mt-3 text-center text-[14px] text-[#555] break-all">
            {hasValue ? `問題の値: ${String(detail.value)}` : '値が空です。'}
          </p>
        )}
        <p className="mt-4 text-center text-[12px]">
          CSV
          {detail?.rowNumber && (
            <span>
              の
              <span className="font-bold text-sm">
                {detail?.rowNumber}行目
              </span>
            </span>
          )}
          を修正して再度アップロードしてください。
        </p>
      </div>
      <button
        onClick={onClose}
        className="border p-2 rounded-[25px] w-[150px] mt-[25px] border-[#666]"
      >
        <p className="text-[#666] font-bold">戻る</p>
      </button>
    </Modal>
  )
}
